import {Component, OnInit, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {Subscription} from 'rxjs';

import {ImageUrls} from '../../../shared/constants/ImageUrls';
import {RouteUrls} from '../../../shared/constants/RouteUrls';
import {PermissionLink} from '../../../shared/permission/PermissionLink';
import {Actions, ApiEndpoints} from '../../../shared/constants/ApiEndpoints';
import {TokenService} from '../../../shared/services/token.service';
import {RequestMethod, RestService} from '../../../shared/services/rest.service';
import {WebSocketService} from '../../../shared/services/web-socket.service';
import {UserService} from '../../../shared/services/user.service';
import {CookieUtils} from '../../../shared/utils/CookieUtils';
import {GeneralConstant} from '../../../shared/constants/GeneralConstant';
import {Partner} from '../../../shared/models/res/partner';
import {MenuItem} from '../../../shared/models/menu-item';
import {MyProfileComponent} from '../my-profile/my-profile.component';
import {NotificationsComponent} from '../notifications/notifications/notifications.component';
import {ConfirmModalComponent} from '../../shared/modules/confirm-modal/confirm-modal.component';
import {LayoutService} from '../layout.service';
import {sideMenuItems} from '../side-menu/side-menu-items';
import {canConnectQbo, navPermission, primaryNavItems, qboNavItem, NavItem} from './nav-items';
import {checkForPermission, checkForPermissionWithExceptions, checkForPermissionWithFacility} from '../../../shared/permission/check-permission';
import {environment} from '../../../../environments/environment';
import {DesignPreferenceService} from '../../../shared/services/design-preference.service';
import {allowedSearchModes, PermissionEntry, resolveSearch, SEARCH_PREFIXES, SearchMode} from './search-modes';

export interface SearchResult {
  title: string;
  url: string;
  icon: string;
  customIcon: string;
  breadcrumb: string;
  subtitle?: string;
  type?: SearchMode;
}

@Component({
  selector: 'app-unified-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class UnifiedSidebarComponent implements OnInit, OnDestroy {
  // Logo & user
  logo = ImageUrls.LOGO;
  avatar = ImageUrls.AVATAR;
  bell = ImageUrls.BELL;
  user: Partner = new Partner();

  // Navigation
  primaryNavItems = primaryNavItems;
  qboNavItem = qboNavItem;
  navPermission = navPermission;
  activeNavItem: NavItem = null;
  subMenuItems: MenuItem[] = [];
  currentUrl = '';

  // Sidebar state
  collapsed = false;

  // Search
  searchQuery = '';
  searchResults: SearchResult[] = [];
  showSearchResults = false;
  searchMode: SearchMode = 'module';
  searchLoading = false;
  allowedModes: SearchMode[] = ['module'];
  searchPlaceholder = 'Search...';
  private searchTimer: any;

  // Role shown under the user's name
  roleLabel = '';

  // Permissions (for QBO)
  qboConnected = true;
  qboPermission = PermissionLink.appendLink(ApiEndpoints.ACCOUNT.QBO, 'connect');

  // Notifications
  notificationCount = 0;

  // Paths
  logoutPath = '/' + RouteUrls.LOGOUT;

  // Internal
  private layoutSubscription: Subscription;
  private routerSubscription: Subscription;

  showRequestTab = true;

  constructor(
    private router: Router,
    private restService: RestService,
    private tokenService: TokenService,
    private dialog: MatDialog,
    private stomp: WebSocketService,
    private userService: UserService,
    private layoutService: LayoutService,
    private designPreference: DesignPreferenceService
  ) {}

  ngOnInit() {
    this.checkForNewRelease();
    this.setUpSearchModes();
    this.roleLabel = (this.tokenService.getRolesFromToken() || [])[0] || '';

    // WebSocket notifications (from HeaderLoggedComponent)
    this.stomp.connect();
    this.stomp.stompClient.onConnect = () => {
      this.stomp.stompClient.subscribe('/notification/count', (message): any => {
        if (this.tokenService.getUserFromToken().username === message.body) {
          if (!this.userService.getUserUUID()) {
            CookieUtils.removeCookie(GeneralConstant.TOKEN);
            CookieUtils.removeCookie(GeneralConstant.REFRESH_TOKEN);
            localStorage.clear();
            location.reload();
          }
        } else {
          this.getNewNotificationsCount();
        }
      });
      this.stomp.stompClient.subscribe('/notification/session_expired', (message): any => {
        if (this.tokenService.getUserFromToken().id === message.body) {
          this.dialog.open(ConfirmModalComponent, {
            data: {
              fullName: 'The session has already expired.',
              name: '',
              type: 'OK',
              removeQuestionMark: true,
              content: 'The session has already expired.'
            }
          });
          CookieUtils.removeCookie(GeneralConstant.TOKEN);
          CookieUtils.removeCookie(GeneralConstant.REFRESH_TOKEN);
          localStorage.clear();
          location.reload();
        }
      });
    };

    this.getMyProfile();
    this.getNewNotificationsCount();

    // QBO check (from HeaderLoggedComponent)
    if (canConnectQbo(this.tokenService.getRolesFromToken() || [])) {
      this.getQboConnected();
    }

    // Route-based sub-menu (from SideMenuComponent)
    this.currentUrl = this.router.url;
    this.updateActiveNavAndSubMenu(this.currentUrl);

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
        this.updateActiveNavAndSubMenu(this.currentUrl);
      }
    });
  }

  ngOnDestroy() {
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // --- Sub-menu logic (from SideMenuComponent) ---

  updateActiveNavAndSubMenu(url: string) {
    // Determine which primary nav item is active
    this.activeNavItem = null;
    for (const item of this.primaryNavItems) {
      if (url.startsWith(item.route)) {
        this.activeNavItem = item;
        break;
      }
    }

    // Set sub-menu items
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
      this.layoutSubscription = null;
    }

    this.layoutSubscription = this.layoutService.getItemMapper().subscribe(func => {
      this.subMenuItems = [];
      Object.keys(sideMenuItems).forEach(value => {
        if (url.startsWith(value)) {
          const sideMenuItemList = sideMenuItems[value].filter(
            it => !it.permissionLink || checkForPermissionWithExceptions(it.permissionLink, this.tokenService.getRolesFromToken(), it.exceptions)
          );
          this.subMenuItems = func(JSON.parse(JSON.stringify(sideMenuItemList)));
          this.subMenuItems = this.subMenuItems.filter(
            it => it.filterWithFacility ? checkForPermissionWithFacility(it.permissionLink, it.facilityId) : true
          );
        }
        this.showRequestTab = true;
      });
      this.subMenuItems = func(JSON.parse(JSON.stringify(this.subMenuItems)));
    });
  }

  isSubItemActive(url: string): boolean {
    const segments = url.split(':pathVariable/');
    let pattern = '';
    for (const segment of segments) {
      pattern = `${pattern}(.*?)${segment}`;
    }
    const reg = new RegExp(pattern);
    return !!this.currentUrl.match(reg);
  }

  navigateSideMenu(url: string) {
    if (url.includes(RouteUrls.APPLICATION.APPLY_RESET)) {
      this.restService.request<any>(RequestMethod.GET, ApiEndpoints.ACCR.APPLICATION,
        Actions.BASE_ACTIONS.READ + `/number_of_unfinished_apps`).subscribe(
        response => {
          if (response.success && !response.data) {
            this.showUnlimitedRequestDialog();
            return;
          } else {
            this.router.navigateByUrl(url);
          }
        }
      );
    } else {
      this.router.navigateByUrl(url);
    }
  }

  showUnlimitedRequestDialog() {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        fullName: 'Approval Required',
        name: '',
        type: 'Yes',
        removeQuestionMark: true,
        body: 'Request Limit Removal',
        content: 'You have reached the limit of new applications!'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.delete) {
        const data = {};
        this.restService.request<any>(
          RequestMethod.POST,
          ApiEndpoints.ACCR.APPLICATION,
          Actions.ACCR_APPLICATION.CREATE_REQUEST,
          {body: data}
        ).subscribe();
      }
    });
  }

  // --- Search ---

  private setUpSearchModes() {
    let permissions: PermissionEntry[] = [];
    try {
      permissions = JSON.parse(localStorage.getItem(GeneralConstant.PERMISSIONS)) || [];
    } catch (e) {
      permissions = [];
    }
    this.allowedModes = allowedSearchModes(permissions, this.tokenService.getRolesFromToken() || []);
    const prefixes = SEARCH_PREFIXES.filter(p => this.allowedModes.includes(p.mode)).map(p => p.prefix);
    this.searchPlaceholder = prefixes.length ? `Search... (${prefixes.join(' ')})` : 'Search...';
  }

  onSearchInput() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    const raw = this.searchQuery.trim();
    if (!raw) {
      this.searchResults = [];
      this.showSearchResults = false;
      this.searchMode = 'module';
      this.searchLoading = false;
      return;
    }

    const resolved = resolveSearch(raw, this.allowedModes);
    this.searchMode = resolved.mode;

    if (resolved.mode !== 'module') {
      const apiQuery = resolved.query.toLowerCase();
      if (!apiQuery) {
        this.searchResults = [];
        this.showSearchResults = true;
        this.searchLoading = false;
        return;
      }
      this.debouncedApiSearch(resolved.mode, apiQuery);
      return;
    }

    // Normal module search
    this.searchLoading = false;
    const query = resolved.query.toLowerCase();

    const results: SearchResult[] = [];

    for (const nav of this.primaryNavItems) {
      if (nav.permissionLink && !checkForPermission(nav.permissionLink)) {
        continue;
      }
      const matchesTitle = nav.title.toLowerCase().includes(query);
      const matchesKeywords = nav.keywords.some(k => k.toLowerCase().includes(query));

      if (matchesTitle || matchesKeywords) {
        results.push({
          title: nav.title,
          url: nav.route,
          icon: nav.icon,
          customIcon: null,
          breadcrumb: null,
          type: 'module'
        });
      }

      if (nav.sideMenuKey && sideMenuItems[nav.sideMenuKey]) {
        for (const sub of sideMenuItems[nav.sideMenuKey]) {
          if (sub.permissionLink && !checkForPermission(sub.permissionLink)) {
            continue;
          }
          if (sub.title.toLowerCase().includes(query)) {
            results.push({
              title: sub.title,
              url: sub.url,
              icon: sub.icon || null,
              customIcon: sub.customIcon || null,
              breadcrumb: nav.title,
              type: 'module'
            });
          }
          if (sub.subItems) {
            for (const nested of sub.subItems) {
              if (nested.permissionLink && !checkForPermission(nested.permissionLink)) {
                continue;
              }
              if (nested.title.toLowerCase().includes(query)) {
                results.push({
                  title: nested.title,
                  url: nested.url,
                  icon: nested.icon || null,
                  customIcon: nested.customIcon || null,
                  breadcrumb: nav.title + ' > ' + sub.title,
                  type: 'module'
                });
              }
            }
          }
        }
      }
    }

    this.searchResults = results;
    this.showSearchResults = results.length > 0;
  }

  private debouncedApiSearch(mode: SearchMode, query: string) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchLoading = true;
    this.showSearchResults = true;

    this.searchTimer = setTimeout(() => {
      switch (mode) {
        case 'facility':
          this.searchFacilities(query);
          break;
        case 'survey':
          this.searchSurveys(query);
          break;
        case 'invoice':
          this.searchInvoices(query);
          break;
        case 'user':
          this.searchUsers(query);
          break;
      }
    }, 350);
  }

  private searchFacilities(query: string) {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.RES.FACILITY,
      Actions.BASE_ACTIONS.READ,
      { params: { search: query, page: 0, size: 15 } }
    ).subscribe(response => {
      this.searchLoading = false;
      if (response.success && response.data) {
        const items = response.data.items || [];
        this.searchResults = items.map((f: any) => ({
          title: f.facilityName || 'Unnamed Facility',
          subtitle: 'ID: ' + f.id,
          url: `/facility/update/${f.id}`,
          icon: 'business',
          customIcon: null,
          breadcrumb: f.city ? (f.city + (f.state ? ', ' + f.state : '')) : null,
          type: 'facility' as const
        }));
      } else {
        this.searchResults = [];
      }
      this.showSearchResults = true;
    });
  }

  private searchSurveys(query: string) {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.SURVEY.SURVEY,
      'read/active_surveys',
      { params: { page: 0, size: 15, search: query } }
    ).subscribe(response => {
      this.searchLoading = false;
      if (response.success && response.data) {
        const items = response.data.items || response.data || [];
        this.searchResults = items.map((s: any) => ({
          title: s.surveyType || s.survey_type || s.type || 'Survey',
          subtitle: 'ID: ' + (s.id || ''),
          url: `/survey/survey-management/${s.id}/information`,
          icon: 'assignment',
          customIcon: null,
          breadcrumb: s.facilityName || s.facility_name || s.legalBusinessName || null,
          type: 'survey' as const
        }));
      } else {
        this.searchResults = [];
      }
      this.showSearchResults = true;
    });
  }

  private searchInvoices(query: string) {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.ACCOUNT.INVOICE,
      Actions.BASE_ACTIONS.READ,
      { params: { page: 0, size: 15, search: query } }
    ).subscribe(response => {
      this.searchLoading = false;
      if (response.success && response.data) {
        const items = response.data.items || response.data || [];
        this.searchResults = items.map((inv: any) => ({
          title: inv.invoiceNumber || inv.invoice_number || inv.number || 'Invoice #' + (inv.id || ''),
          subtitle: inv.status || inv.invoiceStatus || '',
          url: `/financial/invoices/${inv.id}/update`,
          icon: 'receipt',
          customIcon: null,
          breadcrumb: inv.facilityName || inv.facility_name || inv.partnerName || inv.partner_name || null,
          type: 'invoice' as const
        }));
      } else {
        this.searchResults = [];
      }
      this.showSearchResults = true;
    });
  }

  private searchUsers(query: string) {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.RES.USER,
      Actions.BASE_ACTIONS.READ,
      { params: { page: 0, size: 15, search: query } }
    ).subscribe(response => {
      this.searchLoading = false;
      if (response.success && response.data) {
        const items = response.data.items || response.data || [];
        this.searchResults = items.map((u: any) => ({
          title: u.name || u.contactName || u.contact_name || ((u.firstName || '') + ' ' + (u.lastName || '')).trim() || u.username || 'User',
          subtitle: u.email || u.username || '',
          url: `/account-settings/users-form/${u.id}`,
          icon: 'person',
          customIcon: null,
          breadcrumb: u.groupName || u.group_name || u.roleName || null,
          type: 'user' as const
        }));
      } else {
        this.searchResults = [];
      }
      this.showSearchResults = true;
    });
  }

  onSearchResultClick(result: SearchResult) {
    this.router.navigateByUrl(result.url);
    this.searchQuery = '';
    this.searchResults = [];
    this.showSearchResults = false;
  }

  onSearchBlur() {
    setTimeout(() => {
      this.showSearchResults = false;
    }, 200);
  }

  onSearchFocus() {
    if (this.collapsed) {
      this.collapsed = false;
    }
    if (this.searchResults.length > 0) {
      this.showSearchResults = true;
    }
  }

  // --- Sidebar toggle ---

  toggleSidebar() {
    this.collapsed = !this.collapsed;
  }

  expandSidebarForSearch() {
    this.collapsed = false;
  }

  // --- Profile / Notifications (from HeaderLoggedComponent) ---

  myProfile() {
    const dialogRef = this.dialog.open(MyProfileComponent, {
      data: {},
      panelClass: 'my-profile-modal-wrap'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.getMyProfile();
    });
  }

  switchToClassicDesign() {
    this.designPreference.setNewDesign(false);
  }

  myNotifications() {
    const dialogRef = this.dialog.open(NotificationsComponent, {
      data: {},
      panelClass: 'notifications-list'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.notificationCount = 0;
    });
  }

  getMyProfile() {
    if (this.tokenService.getUserFromToken()) {
      this.restService.request<any>(
        RequestMethod.GET,
        ApiEndpoints.RES.PARTNER,
        'read-profile' + '/' + this.tokenService.getUserFromToken().contact.id
      ).subscribe(response => {
        if (response.success) {
          this.user = response.data;
          this.avatar = this.user && this.user.logo ? this.user.logo.filePath : ImageUrls.AVATAR;
        }
      });
    }
  }

  getNewNotificationsCount() {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.NOTIFICATION.NOTIFICATION,
      `read/count`,
      {
        params: {
          partnerId: this.tokenService.getUserFromToken().contact.id
        }
      }
    ).subscribe(response => {
      this.notificationCount = response.data;
    });
  }

  // --- QBO (from HeaderLoggedComponent) ---

  getQboConnected() {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.ACCOUNT.QBO,
      `read/authenticated`
    ).subscribe(response => {
      this.qboConnected = response.data;
    });
  }

  connectToQbo() {
    this.restService.request<any>(
      RequestMethod.GET,
      ApiEndpoints.ACCOUNT.QBO,
      `connect`
    ).subscribe(response => {
      window.location.replace(response.data.url);
    });
  }

  // --- Release check (from HeaderLoggedComponent) ---

  checkForNewRelease() {
    const currentVersion = environment.version || this.getVersionFromEnvFile();
    const previousVersion = localStorage.getItem('releaseVersion');

    if (previousVersion != null && previousVersion !== currentVersion) {
      this.clearCookies();
      localStorage.setItem('releaseVersion', currentVersion);
    } else {
      localStorage.setItem('releaseVersion', currentVersion);
    }
  }

  getVersionFromEnvFile() {
    try {
      const envContent = require('fs').readFileSync('/path/to/version.env', 'utf-8');
      const match = envContent.match(/CURRENT_RELEASE_VERSION=(.*)/);
      return match ? match[1] : 'unknown';
    } catch (e) {
      console.error('Failed to read version from file', e);
      return 'unknown';
    }
  }

  clearCookies() {
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    window.location.reload();
  }

  get displayName(): string {
    if (this.user?.name) {
      return this.user.name;
    }
    if (this.user?.firstName || this.user?.lastName) {
      return ((this.user.firstName || '') + ' ' + (this.user.lastName || '')).trim();
    }
    return '';
  }
}
